import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../../supabaseClient';
import api from '../../api';
import TemplatePicker from './TemplatePicker';
import usePlayerMeta, { normName, abbrevInjury } from './usePlayerMeta';
import { vibrate } from '../../haptics';
import { slotOnClock, teamsByUser, TeamAvatar, NHL_COLORS, teamLogo, useFitWidth, capFits, fmtCap, payrollOf, CapMeter, NHL_CAP_MIN, pkey, draftPts, LAST_SEASON } from './poolFx';



export default function DraftRoom({ poolId, userId, onBack, lang = 'en' }) {
  const t = lang === 'fr' ? TR.fr : TR.en;
  const [pool, setPool] = useState(null);
  const [members, setMembers] = useState([]);
  const [picks, setPicks] = useState([]);
  const [allPlayers, setAllPlayers] = useState([]);
  const [playersLoading, setPlayersLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [posFilter, setPosFilter] = useState('ALL');
  const [teamFilter, setTeamFilter] = useState('ALL');
  const [hideInjured, setHideInjured] = useState(false);
  const [showMyQueue, setShowMyQueue] = useState(false);
  const [queueRanks, setQueueRanks] = useState(null); // Map<player_id, rank> or null if no template
  const { playoffTeams, injuryMap } = usePlayerMeta();
  const [picking, setPicking] = useState(false);
  const [error, setError] = useState('');
  const [now, setNow] = useState(Date.now());
  const [mobileTab, setMobileTab] = useState('players'); // phone layout: players | draft | teams
  const [sideTab, setSideTab] = useState('draft');        // side column: draft | teams
  const [viewTeam, setViewTeam] = useState(undefined);      // Teams tab: which team is shown (undefined = the team on the clock)

  const bodyRef = useRef(null);

  // Laptops: the whole Draft Room scales as one piece down to tablet width
  const roomRef = useFitWidth(1320, [!!pool]);

  const fetchState = useCallback(async () => {
    const [{ data: poolRow }, { data: memberRows }, { data: pickRows }] = await Promise.all([
      supabase.from('pools').select('*').eq('id', poolId).single(),
      supabase.from('pool_members').select('user_id, draft_slot, quit_at, active_template_id, profiles(username)').eq('pool_id', poolId).order('draft_slot', { ascending: true }),
      supabase.from('pool_picks').select('*').eq('pool_id', poolId).order('pick_number', { ascending: true }),
    ]);
    if (poolRow) setPool(poolRow);
    if (memberRows) setMembers(memberRows);
    if (pickRows) setPicks(pickRows);
  }, [poolId]);

  useEffect(() => { fetchState(); }, [fetchState]);

  // Fetch the user's active template entries whenever their active_template_id changes.
  const myActiveTemplateId = members.find((m) => m.user_id === userId)?.active_template_id || null;
  useEffect(() => {
    if (!myActiveTemplateId) { setQueueRanks(null); return; }
    let active = true;
    (async () => {
      const { data } = await supabase
        .from('user_queue_entries')
        .select('player_id, rank')
        .eq('template_id', myActiveTemplateId);
      if (!active) return;
      const map = new Map();
      (data || []).forEach((e) => map.set(e.player_id, e.rank));
      setQueueRanks(map);
    })();
    return () => { active = false; };
  }, [myActiveTemplateId]);

  useEffect(() => {
    const channel = supabase
      .channel(`draft-${poolId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pools', filter: `id=eq.${poolId}` }, fetchState)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_picks', filter: `pool_id=eq.${poolId}` }, fetchState)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'pool_members', filter: `pool_id=eq.${poolId}` }, fetchState)
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [poolId, fetchState]);

  useEffect(() => {
    // keeps refreshing while the server is still building the NHL list (after a restart)
    return api.watchRosters((players, updating) => {
      if (players.length) setAllPlayers(players);
      if (players.length || !updating) setPlayersLoading(false);
    }, () => { setError(t.errRosters); setPlayersLoading(false); });
  }, [t.errRosters]);

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  // same NHL id = same player, whatever team prefix the id has
  const pickedIds = useMemo(() => { const set = new Set(picks.map((p) => pkey(p.player_id))); return { has: (id) => set.has(pkey(id)) }; }, [picks]);

  const teams = useMemo(() => {
    const set = new Set(allPlayers.map((p) => p.team).filter(Boolean));
    return ['ALL', ...Array.from(set).sort()];
  }, [allPlayers]);

  const isPlayoffPool = pool?.format === 'playoff';

  const pickInfoById = useMemo(() => {
    const map = new Map();
    picks.forEach((p) => {
      const m = members.find((x) => x.user_id === p.user_id);
      map.set(pkey(p.player_id), { pickNumber: p.pick_number, username: m?.profiles?.username || '—' });
    });
    return { get: (id) => map.get(pkey(id)) };
  }, [picks, members]);

  const filteredPlayers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const passOtherFilters = (p) =>
      p.position !== 'T' &&
      (posFilter === 'ALL' || p.position === posFilter) &&
      (teamFilter === 'ALL' || p.team === teamFilter) &&
      (!q || p.name.toLowerCase().includes(q)) &&
      (!isPlayoffPool || !playoffTeams || playoffTeams.size === 0 || playoffTeams.has(p.team)) &&
      (!hideInjured || !injuryMap || !injuryMap.has(normName(p.name)));

    if (showMyQueue && queueRanks) {
      // Keep drafted players visible in queue view so user can see who got sniped.
      return allPlayers
        .filter((p) => queueRanks.has(p.id))
        .filter(passOtherFilters)
        .sort((a, b) => (queueRanks.get(a.id) || 999) - (queueRanks.get(b.id) || 999));
    }

    return allPlayers
      .filter((p) => !pickedIds.has(p.id))
      .filter(passOtherFilters)
      .sort((a, b) => draftPts(b) - draftPts(a));   // last season's points
  }, [allPlayers, pickedIds, posFilter, teamFilter, search, isPlayoffPool, playoffTeams, hideInjured, injuryMap, showMyQueue, queueRanks]);


  const n = members.length;
  const rosterSize = pool?.roster_size || 16;
  const totalPicks = n * rosterSize;
  const currentPick = pool?.current_pick_index || 0;
  const teamRound = !!pool?.team_round;                 // picks 1..n = team round (reverse order)
  const inTeamRound = teamRound && currentPick >= 1 && currentPick <= n;
  const currentSlot = slotOnClock(currentPick, n, teamRound);
  const currentMember = members.find((m) => m.draft_slot === currentSlot);
  const isMyTurn = currentMember?.user_id === userId;
  const playerRosterSize = teamRound ? rosterSize - 1 : rosterSize; // roster_size includes the team
  const round = currentPick > 0 ? Math.floor((currentPick - (teamRound ? n : 0) - 1) / n) + 1 : 0;

  const picksByUser = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.user_id] = []; });
    picks.forEach((p) => {
      if (!map[p.user_id]) map[p.user_id] = [];
      map[p.user_id].push(p);
    });
    return map;
  }, [members, picks]);

  const deadlineMs = pool?.pick_deadline ? new Date(pool.pick_deadline).getTime() : null;
  const secondsLeft = deadlineMs ? Math.max(0, Math.ceil((deadlineMs - now) / 1000)) : null;
  const advanceAttemptedRef = useRef(null);

  useEffect(() => {
    if (secondsLeft === null || secondsLeft > 0) return;
    if (pool?.status !== 'drafting') return;
    if (pool?.mode === 'season') return;   // GM Season: the server picks for you when time's up (never a skipped pick)
    const key = `${poolId}-${currentPick}`;
    if (advanceAttemptedRef.current === key) return;
    advanceAttemptedRef.current = key;
    supabase.rpc('advance_if_expired', { p_pool_id: poolId });
  }, [secondsLeft, poolId, currentPick, pool?.status]);

  // vibrations (phones / tablets): it's your pick · time's up (only while drafting)
  const wasMyTurnRef = useRef(false);
  useEffect(() => {
    if (pool?.status !== 'drafting') { wasMyTurnRef.current = false; return; }
    if (isMyTurn && !wasMyTurnRef.current) vibrate('yourTurn');
    wasMyTurnRef.current = isMyTurn;
  }, [isMyTurn, pool?.status]);
  const buzzedRef = useRef(null);
  useEffect(() => {
    if (pool?.status !== 'drafting' || secondsLeft !== 0) return;
    const key = `${poolId}-${currentPick}`;
    if (buzzedRef.current === key) return;
    buzzedRef.current = key;
    vibrate('timeUp');
  }, [secondsLeft, poolId, currentPick, pool?.status]);

  const pendingId = pool?.pending_pick_player_id || null;
  const pendingName = pool?.pending_pick_player_name || null;
  const pendingTeam = pool?.pending_pick_team || null;
  const pendingPos = pool?.pending_pick_position || null;
  const hasPending = !!pendingId;

  const handleSelect = async (player) => {
    if (!isMyTurn || picking) return;
    setError(''); setPicking(true);
    const { data, error: rpcErr } = await supabase.rpc('select_player', {
      p_user_id: userId,
      p_pool_id: poolId,
      p_player_id: player.id,
      p_player_name: player.name,
      p_team: player.team,
      p_position: player.position,
    });
    setPicking(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errPick); return; }
  };

  const handleConfirm = async () => {
    if (!isMyTurn || picking) return;
    setError(''); setPicking(true);
    const { data, error: rpcErr } = await supabase.rpc('confirm_pick', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setPicking(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errPick); return; }
  };

  const isCreator = pool?.creator_id === userId;

  const handleCancelPool = async () => {
    if (!window.confirm(t.confirmCancel)) return;
    setError(''); setPicking(true);
    const { data, error: rpcErr } = await supabase.rpc('cancel_pool', {
      p_user_id: userId, p_pool_id: poolId,
    });
    setPicking(false);
    if (rpcErr) { setError(rpcErr.message); return; }
    if (!data?.success) { setError(data?.error || t.errCancel); return; }
    onBack?.();
  };

  // ── Pick notification: small, non-blocking toast in the corner ──
  const [reveal, setReveal] = useState(null);
  const seenPicksRef = useRef(null);              // null until first load, so old picks don't replay

  useEffect(() => {
    if (seenPicksRef.current === null) { if (pool) seenPicksRef.current = picks.length; return undefined; }
    if (picks.length <= seenPicksRef.current) { seenPicksRef.current = picks.length; return undefined; }
    seenPicksRef.current = picks.length;
    const latest = picks[picks.length - 1];
    if (!latest) return undefined;
    const m = members.find((x) => x.user_id === latest.user_id);
    setReveal({ ...latest, username: m?.profiles?.username || '—', mine: latest.user_id === userId });
    vibrate('pick');
    const id = setTimeout(() => setReveal(null), 3500);
    return () => clearTimeout(id);
  }, [picks, members, pool, userId]);

  if (!pool) return <div className="draft-room">{t.loading}</div>;

  if (pool.status === 'active' || pool.status === 'completed') {
    return (
      <div className="draft-room">
        <button className="pool-lobby-back" onClick={onBack}>← {t.back}</button>
        <h2>{pool.name}</h2>
        <p>{t.draftComplete}</p>
      </div>
    );
  }

  const fr = lang === 'fr';
  const pickSeconds = pool.pick_seconds || 60;
  const timePct = secondsLeft !== null ? Math.max(0, Math.min(100, (secondsLeft / pickSeconds) * 100)) : 0;
  const upNext = Array.from({ length: 5 }, (_, i) => currentPick + i)
    .filter((n) => n >= 1 && n <= totalPicks)
    .map((n) => ({ n, member: members.find((m) => m.draft_slot === slotOnClock(n, members.length, teamRound)) }));
  const recent = picks.slice().reverse().slice(0, 5);    // latest 5 only
  const myPicks = picksByUser[userId] || [];
  const myPlayerPicks = myPicks.filter((p) => p.position !== 'T');
  const teamOf = teamsByUser(picks);   // user_id -> their NHL team (their identity in the pool)

  // ── Position slots (F / D / G). NULL on the pool = any position (older pools) ──
  // T = drafted NHL team — always 1 per lineup
  const slotLimit = { F: pool.slots_f ?? null, D: pool.slots_d ?? null, G: pool.slots_g ?? null, T: pool.slots_t ?? 1 };
  const hasSlots = slotLimit.F != null || slotLimit.D != null || slotLimit.G != null;
  const countPos = (list) => list.reduce((c, p) => { c[p.position] = (c[p.position] || 0) + 1; return c; }, { F: 0, D: 0, G: 0, T: 0 });
  const myCounts = countPos(myPicks);
  const isFullFor = (pos) => (pos === 'T' || hasSlots) && slotLimit[pos] != null && (myCounts[pos] || 0) >= slotLimit[pos];

  // ── Salary cap: hard = a player must fit (room kept for the spots left), penalty = warn only ──
  const hasCap = !!pool.cap_mode && pool.cap_amount != null;
  const capById = new Map(allPlayers.map((p) => [pkey(p.id), p.capHit ?? NHL_CAP_MIN]));
  const withCap = (list) => list.map((p) => ({ ...p, cap_hit: p.cap_hit ?? capById.get(pkey(p.player_id)) ?? NHL_CAP_MIN }));
  const myPayroll = payrollOf(withCap(myPlayerPicks));
  const fitsCap = (p) => capFits(pool, withCap(myPlayerPicks), playerRosterSize, p);
  const putsOver = (p) => hasCap && p.position !== 'T' && myPayroll + (p.capHit ?? NHL_CAP_MIN) > pool.cap_amount;
  // GM Season rule "3 of your own" (pool.home_min): pick freely, but when the picks you have left = the own-team
  // players you still need, only players from the NHL team you picked can be taken (the database enforces it too)
  const homeMin = pool.home_min || 0;
  const myHome = teamOf[userId]?.abbrev || null;
  const homeHave = myPlayerPicks.filter((p) => p.team === myHome).length;
  const homeNeed = homeMin > 0 && myHome ? Math.max(0, homeMin - homeHave) : 0;
  const homeForced = homeNeed > 0 && playerRosterSize - myPlayerPicks.length <= homeNeed;
  const homeBlocked = (p) => homeForced && p.position !== 'T' && p.team !== myHome;
  // "Pick from my list" skips positions you've already filled
  const autoPick = (() => {
    if (!queueRanks || queueRanks.size === 0) return null;
    const ids = [...queueRanks.entries()].sort((a, b) => a[1] - b[1]).map(([id]) => id);
    for (const id of ids) {
      if (pickedIds.has(id)) continue;
      const pl = allPlayers.find((x) => x.id === id);
      if (pl && !isFullFor(pl.position) && fitsCap(pl) && !homeBlocked(pl)) return { ...pl, rank: queueRanks.get(id) };
    }
    return null;
  })();

  // ── Lineup board (Teams tab, left panel) ──
  const lineupId = viewTeam ?? userId;
  const lineupMember = members.find((m) => m.user_id === lineupId);
  const lineupPicks = picksByUser[lineupId] || [];
  const POS_GROUPS = [
    ['F', fr ? 'Attaquants' : 'Forwards', 3],
    ['D', fr ? 'Défenseurs' : 'Defence', 2],
    ['G', fr ? 'Gardiens' : 'Goalies', 2],
  ];
  // how many slot lines the board needs — big lineups get slightly smaller slots so it always fits
  const lineupLines = POS_GROUPS.reduce((n, [pos, , per]) => {
    const cnt = hasSlots ? (slotLimit[pos] ?? 0) : lineupPicks.filter((p) => p.position === pos).length;
    return n + Math.ceil(cnt / per);
  }, 0);
  // ── Team round: pick your NHL team (left panel) ──
  const takenBy = {};
  picks.forEach((p) => { if (p.position === 'T') takenBy[p.player_id] = p.user_id; });
  const nhlTeams = allPlayers.filter((p) => p.position === 'T').sort((a, b) => draftPts(b) - draftPts(a));
  const teamGridEl = (
    <div className="dr-teampick">
      <div className="dr-teampick-head">
        <span className="dr-lineup-name">{fr ? 'Choisis ton équipe' : 'Pick your team'}</span>
        <span className="dr-lineup-count">{fr ? 'Elle devient TON équipe · points au classement' : 'It becomes YOUR team · scores its standings points'}</span>
      </div>
      <div className="dr-teampick-grid">
        {nhlTeams.map((tm) => {
          const owner = takenBy[tm.id];
          const ownerName = owner ? members.find((m) => m.user_id === owner)?.profiles?.username : null;
          const staged = pendingId === tm.id;
          const c = NHL_COLORS[tm.team];
          return (
            <button
              key={tm.id}
              className={`dr-teamcard ${owner ? 'taken' : ''} ${staged ? 'staged' : ''} ${owner === userId ? 'mine' : ''}`}
              style={c ? { '--team-primary': c[0], '--team-secondary': c[1] } : undefined}
              disabled={!isMyTurn || picking || !!owner}
              onClick={() => { if (owner) return; if (staged) handleConfirm(); else handleSelect(tm); }}
              title={owner ? `${fr ? 'Choisie par' : 'Taken by'} ${ownerName}` : tm.name}
            >
              <img className="dr-teamcard-logo" alt="" src={teamLogo(tm.team)} loading="lazy"
                onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
              <span className="dr-teamcard-name">{tm.name}</span>
              <span className="dr-teamcard-meta">
                {owner ? <b>{ownerName}</b> : <><b>{draftPts(tm)}</b> {tm.lastPoints != null ? `${t.pts} ${LAST_SEASON}` : t.pts}</>}
              </span>
            </button>
          );
        })}
        {nhlTeams.length === 0 && <div className="pools-empty">{t.loadingPlayers}</div>}
      </div>
    </div>
  );

  const lineupEl = (
    <div
      className={`dr-lineup ${lineupLines > 5 ? 'compact' : ''} ${teamOf[lineupId] ? 'has-team' : ''}`}
      style={teamOf[lineupId] && NHL_COLORS[teamOf[lineupId].abbrev]
        ? { '--team-primary': NHL_COLORS[teamOf[lineupId].abbrev][0], '--team-secondary': NHL_COLORS[teamOf[lineupId].abbrev][1] }
        : undefined}
    >
      <div className="dr-lineup-head">
        <TeamAvatar team={teamOf[lineupId]} name={lineupMember?.profiles?.username} size={46} />
        <span className="dr-lineup-who">
          <span className="dr-lineup-name">{lineupMember?.profiles?.username || '—'}</span>
          <span className="dr-lineup-team">{teamOf[lineupId]?.name || (fr ? 'Équipe à choisir' : 'No team yet')}</span>
        </span>
        {lineupId === userId && <span className="dr-upnext-tag">{fr ? 'toi' : 'you'}</span>}
        {hasCap && (() => {
          const pay = payrollOf(withCap(lineupPicks.filter((p) => p.position !== 'T')));
          return <span className={`dr-lineup-cap ${pay > pool.cap_amount ? 'over' : ''}`}>{fmtCap(pay)}<small> / {fmtCap(pool.cap_amount)}</small></span>;
        })()}
        <span className="dr-lineup-count">#{lineupMember?.draft_slot} · {lineupPicks.filter((p) => p.position !== 'T').length}/{playerRosterSize}</span>
      </div>
      {POS_GROUPS.map(([pos, label, perLine]) => {
        const got = lineupPicks.filter((p) => p.position === pos).sort((a, b) => a.pick_number - b.pick_number);
        // With slots: always show every slot (filled or open). Without: just what was picked.
        const total = hasSlots ? (slotLimit[pos] ?? 0) : got.length;
        if (total === 0 && got.length === 0) return null;
        return (
          <section key={pos} className="dr-lineup-group">
            <h4 className="dr-card-title">
              {label}
              <span className="dr-card-count">{got.length}{hasSlots ? `/${total}` : ''}</span>
            </h4>
            {/* real hockey lines: forward trios, D pairs, goalies — each line always side by side */}
            <div className="dr-lineup-lines" style={{ '--per-line': perLine }}>
              {(() => {
                const cells = Array.from({ length: Math.max(total, got.length) }).map((_, i) => got[i] || null);
                const lines = [];
                for (let i = 0; i < cells.length; i += perLine) lines.push(cells.slice(i, i + perLine));
                return lines.map((line, li) => (
                  <div key={`${pos}-line-${li}`} className="dr-line">
                    <div className="dr-line-slots">
                      {line.map((p, j) => (p ? (
                        <div key={p.id} className="dr-slot filled">
                          <img className="dr-slot-logo" alt="" src={`https://assets.nhle.com/logos/nhl/svg/${p.team}_light.svg`}
                            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                          <span className="dr-slot-name">{p.player_name}</span>
                          <span className="dr-slot-meta"><b className="dr-slot-pos">{pos}</b> {p.team} · #{p.pick_number}</span>
                        </div>
                      ) : (
                        <div key={`open-${pos}-${li}-${j}`} className="dr-slot open">
                          <span className="dr-slot-plus" aria-hidden="true">+</span>
                          <span className="dr-slot-name">{fr ? 'Place libre' : 'Open slot'}</span>
                          <span className="dr-slot-meta"><b className="dr-slot-pos">{pos}</b></span>
                        </div>
                      )))}
                    </div>
                  </div>
                ));
              })()}
            </div>
          </section>
        );
      })}
    </div>
  );

  return (
    <div ref={roomRef} className={`draft-room dr ${isMyTurn ? 'is-my-turn' : ''}`} data-mtab={mobileTab}>
      {/* pick notification — never blocks clicks */}
      {reveal && (
        <div key={reveal.id} className={`dr-toast ${reveal.mine ? 'mine' : ''}`} role="status" aria-live="polite">
          <span className="dr-toast-n">{reveal.pick_number != null ? `#${reveal.pick_number}` : (fr ? 'ÉQ' : 'TM')}</span>
          <span className="dr-toast-body">
            <b>{reveal.player_name}</b>
            <small>{reveal.position} · {reveal.team} · {reveal.mine ? (fr ? 'ton choix' : 'your pick') : `${fr ? 'par' : 'by'} ${reveal.username}`}</small>
          </span>
        </div>
      )}

      <div className="draft-room-top">
        <button className="pool-lobby-back" onClick={onBack}>← {t.back}</button>
        <div className="draft-room-title">{pool.name}</div>
        {isCreator && (
          <button className="pool-cancel-btn" onClick={handleCancelPool} disabled={picking}>
            {t.cancelPool}
          </button>
        )}
      </div>

      {/* Band + pending pick + (phone) tabs — pinned on phones */}
      <div className="draft-sticky">
        <div className={`dr-band ${isMyTurn ? 'my-turn' : ''}`}>
          <div className="dr-band-round">
            <span className="dr-band-label">{inTeamRound ? (fr ? 'Ronde des équipes' : 'Team round') : `${t.round} ${round}/${playerRosterSize}`}</span>
            {/* teams don't count as draft picks: player picks are numbered from #1 after the team round */}
            <span className="dr-band-pick">
              {inTeamRound
                ? <>{fr ? 'Équipe' : 'Team'} {currentPick}<small>/{n}</small></>
                : <>{t.pick} {currentPick - (teamRound ? n : 0)}<small>/{n * playerRosterSize}</small></>}
            </span>
          </div>
          <div className="dr-band-clock">
            {hasPending ? (
              <>
                <span className="dr-band-label">
                  {isMyTurn ? t.youAreSelecting : `${currentMember?.profiles?.username || '—'} ${t.isSelecting}`}
                </span>
                <span className="dr-band-name">
                  {pendingName} <small className="dr-band-sel-meta">{pendingPos} · {pendingTeam}</small>
                </span>
              </>
            ) : (
              <>
                <span className="dr-band-label">{isMyTurn ? (fr ? "C'est à toi" : "You're up") : t.onClock}</span>
                <span className="dr-band-name">
                  {isMyTurn
                    ? (inTeamRound ? (fr ? 'CHOISIS TON ÉQUIPE' : 'PICK YOUR TEAM') : (fr ? 'FAIS TON CHOIX' : 'MAKE YOUR PICK'))
                    : (currentMember?.profiles?.username || '—')}
                  {!isMyTurn && teamOf[currentMember?.user_id] && <small className="dr-band-sel-meta"> · {teamOf[currentMember.user_id].short}</small>}
                </span>
              </>
            )}
          </div>
          {isMyTurn && hasPending && (
            <button className="dr-band-confirm" onClick={handleConfirm} disabled={picking}>
              {picking ? '...' : `✓ ${t.confirmPick}`}
            </button>
          )}
          {secondsLeft !== null && (
            <div className={`dr-band-timer ${secondsLeft <= 10 ? 'urgent' : ''}`}>
              {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </div>
          )}
          <div className="dr-band-bar"><span style={{ width: `${timePct}%` }} /></div>
        </div>

        {/* Phone only (hidden by CSS on larger screens) */}
        <div className="draft-mobile-tabs" role="tablist">
          {[
            ['players', fr ? 'Repêchage' : 'Draft'],
            ['draft', fr ? 'Choix' : 'Picks'],
            ['teams', fr ? 'Équipes' : 'Teams'],
          ].map(([key, label]) => (
            <button key={key} role="tab" aria-selected={mobileTab === key}
              className={mobileTab === key ? 'active' : ''}
              onClick={() => { setMobileTab(key); setSideTab(key === 'players' ? 'draft' : key); }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && <div className="pool-form-error">{error}</div>}

      <div className="dr-body" ref={bodyRef}>
        {/* ── Players ── */}
        <div className="draft-players-panel dr-players">
          {sideTab === 'teams' ? lineupEl : inTeamRound ? teamGridEl : (<>
          <div className="dr-players-head">
            <TemplatePicker poolId={poolId} userId={userId} lang={lang} />
          </div>
          <div className="draft-filters">
            <input type="text" className="draft-search" placeholder={t.searchPlayer}
              value={search} onChange={(e) => setSearch(e.target.value)} />
            <div className="dr-pos-chips" role="group">
              {[['ALL', fr ? 'Tous' : 'All'], ...['F', 'D', 'G'].map((ps) => [ps, hasSlots && slotLimit[ps] != null ? `${ps} ${myCounts[ps]}/${slotLimit[ps]}` : ps])].map(([v, l]) => (
                <button key={v} className={posFilter === v ? 'active' : ''} onClick={() => setPosFilter(v)}>{l}</button>
              ))}
            </div>
            {homeMin > 0 && myHome && (
              <span className={`dr-home-chip ${homeNeed === 0 ? 'ok' : homeForced ? 'forced' : ''}`} title={fr ? `Au moins ${homeMin} joueurs de ton équipe (${myHome})` : `At least ${homeMin} players from your own team (${myHome})`}>
                {myHome} {Math.min(homeHave, homeMin)}/{homeMin}
              </span>
            )}
            <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)}>
              {teams.map((tm) => (
                <option key={tm} value={tm}>{tm === 'ALL' ? t.allTeams : tm}</option>
              ))}
            </select>
            <label className="draft-filter-toggle">
              <input type="checkbox" checked={hideInjured} onChange={(e) => setHideInjured(e.target.checked)} />
              {t.hideInjured}
            </label>
            {queueRanks && queueRanks.size > 0 && (
              <label className="draft-filter-toggle">
                <input type="checkbox" checked={showMyQueue} onChange={(e) => setShowMyQueue(e.target.checked)} />
                {t.showMyQueue} ({queueRanks.size})
              </label>
            )}
          </div>
          {isPlayoffPool && playoffTeams && playoffTeams.size > 0 && (
            <div className="draft-filter-note">{t.playoffOnlyNote}</div>
          )}
          {isPlayoffPool && playoffTeams && playoffTeams.size === 0 && (
            <div className="draft-filter-note">{t.noPlayoffData}</div>
          )}

          {isMyTurn && autoPick && !hasPending && (
            <button className="draft-autopick-btn" onClick={() => handleSelect(autoPick)} disabled={picking} title={t.autoPickHint}>
              {t.autoPickFromList} #{autoPick.rank} {autoPick.name}
            </button>
          )}

          {playersLoading && <div className="pools-empty">{t.loadingPlayers}</div>}
          {!playersLoading && (
            <div className="draft-players-list">
              {filteredPlayers.slice(0, 200).map((p, idx) => {
                const isStaged = pendingId === p.id;
                const injStatus = injuryMap?.get(normName(p.name));
                const myRank = queueRanks?.get(p.id);
                const pickInfo = pickInfoById.get(p.id);
                const isDrafted = !!pickInfo;
                const posFull = !isDrafted && isFullFor(p.position);
                const overCap = !isDrafted && !posFull && !fitsCap(p);          // hard cap: can't pick him
                const warnOver = !isDrafted && !posFull && !overCap && putsOver(p); // penalties: allowed, but you'd be over
                const notHome = !isDrafted && !posFull && !overCap && homeBlocked(p);  // your last picks must be from your own team
                return (
                  <button
                    key={p.id}
                    className={`draft-player-row dr-prow ${isStaged ? 'staged' : ''} ${isDrafted ? 'drafted' : ''} ${posFull ? 'pos-full' : ''} ${overCap || notHome ? 'over-cap' : ''}`}
                    onClick={() => { if (isDrafted || posFull || overCap || notHome) return; if (isStaged) handleConfirm(); else handleSelect(p); }}
                    disabled={!isMyTurn || picking || isDrafted || posFull || overCap || notHome}
                    title={isDrafted ? `${t.draftedBy} ${pickInfo.username} (#${pickInfo.pickNumber})` : (!isMyTurn ? t.notYourTurn : injStatus ? `${injStatus}` : '')}
                  >
                    {/* rank: best-available order, or your list rank in "show my queue" */}
                    <span className="dr-prow-rank">{showMyQueue && myRank ? `#${myRank}` : idx + 1}</span>
                    <img className="dr-prow-logo" alt="" loading="lazy"
                      src={`https://assets.nhle.com/logos/nhl/svg/${p.team}_light.svg`}
                      onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
                    <span className="dr-prow-main">
                      <span className="dr-prow-name">{p.name}</span>
                      <span className="dr-prow-meta">
                        <b className="dr-slot-pos">{p.position === 'T' ? (fr ? 'ÉQUIPE' : 'TEAM') : p.position}</b>
                        <span className="dr-prow-team">{p.position === 'T' ? (fr ? 'points au classement' : 'standings points') : p.team}</span>
                        {injStatus && <span className="dr-tag inj" title={injStatus}>{abbrevInjury(injStatus)}</span>}
                        {!showMyQueue && myRank && <span className="dr-tag queue" title={t.inYourQueue}>{fr ? 'Ma liste' : 'My list'} #{myRank}</span>}
                        {hasCap && p.position !== 'T' && <span className="dr-tag cap">{fmtCap(p.capHit ?? NHL_CAP_MIN)}</span>}
                        {overCap && <span className="dr-tag overcap">{fr ? 'Ne rentre pas' : 'Doesn’t fit'}</span>}
                        {warnOver && <span className="dr-tag overcap">{fr ? 'Au-dessus du plafond' : 'Over the cap'}</span>}
                        {notHome && <span className="dr-tag overcap">{fr ? `Il te faut un joueur de ${myHome}` : `You need a ${myHome} player`}</span>}
                        {posFull && <span className="dr-tag full">{p.position} {fr ? 'complet' : 'full'}</span>}
                        {isDrafted && <span className="dr-tag taken">{t.draftedBy} {pickInfo.username} · #{pickInfo.pickNumber}</span>}
                      </span>
                    </span>
                    <span className="dr-prow-pts"><b>{draftPts(p)}</b><small>{p.lastPoints != null ? `${t.pts} ${LAST_SEASON}` : t.pts}</small></span>
                  </button>
                );
              })}
              {filteredPlayers.length > 200 && (
                <div className="draft-list-footer">{t.showingFirst} 200 / {filteredPlayers.length}</div>
              )}
              {filteredPlayers.length === 0 && <div className="pools-empty">{t.noPlayers}</div>}
            </div>
          )}
          </>)}
        </div>

        {/* ── Side: sub-tabs  Draft (my picks · up next · latest) | Teams ── */}
        <aside className="dr-side">
          <div className="dr-side-tabs" role="tablist">
            {[['draft', fr ? 'Repêchage' : 'Draft'], ['teams', fr ? 'Équipes' : 'Teams']].map(([key, label]) => (
              <button key={key} role="tab" aria-selected={sideTab === key}
                className={sideTab === key ? 'active' : ''} onClick={() => setSideTab(key)}>
                {label}
              </button>
            ))}
          </div>

          <div className="dr-side-scroll">
            <div className={`dr-layer dr-layer-draft ${sideTab === 'draft' ? 'on' : ''}`}>
                <section className="dr-card dr-mine">
                  <h4 className="dr-card-title">
                    {fr ? 'Mes choix' : 'My picks'}
                    <span className="dr-card-count">{myPlayerPicks.length}/{playerRosterSize}</span>
                  </h4>
                  {hasCap && <CapMeter className="dr-mine-cap" payroll={myPayroll} cap={pool.cap_amount} label={fr ? (pool.cap_mode === 'hard' ? 'Plafond strict' : 'Masse salariale') : (pool.cap_mode === 'hard' ? 'Hard cap' : 'Payroll')} />}
                  {myPlayerPicks.length === 0 ? (
                    <div className="dr-team-empty dr-mine-list" style={{ "--rows": Math.ceil(playerRosterSize / 2) }}>{fr ? 'Aucun choix pour l’instant' : 'No picks yet'}</div>
                  ) : (
                    <div className="dr-mine-list" style={{ "--rows": Math.ceil(playerRosterSize / 2) }}>
                      {myPlayerPicks.map((p) => (
                        <div key={p.id} className="dr-mine-row">
                          <span className="dr-feed-n">{p.pick_number != null ? `#${p.pick_number}` : (fr ? 'ÉQ' : 'TM')}</span>
                          <span className="dr-chip-pos">{p.position}</span>
                          <span className="dr-mine-name">{p.player_name}</span>
                          <span className="dr-mine-team">{p.team}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </section>

                <section className="dr-card dr-upnext">
                  <h4 className="dr-card-title">{fr ? 'À venir' : 'Up next'}</h4>
                  <ol>
                    {upNext.map(({ n, member }, i) => (
                      <li key={n} className={`${i === 0 ? 'now' : ''} ${member?.user_id === userId ? 'me' : ''}`}>
                        <span className="dr-upnext-n">{teamRound && n <= members.length ? (fr ? 'ÉQ' : 'TM') : `#${teamRound ? n - members.length : n}`}</span>
                        <span className="dr-upnext-name"><TeamAvatar team={teamOf[member?.user_id]} name={member?.profiles?.username} size={22} /> {member?.profiles?.username || '—'}</span>
                        {i === 0 && <span className="dr-upnext-tag">{fr ? 'maintenant' : 'now'}</span>}
                        {i !== 0 && member?.user_id === userId && <span className="dr-upnext-tag you">{fr ? 'toi' : 'you'}</span>}
                      </li>
                    ))}
                  </ol>
                </section>

                <section className="dr-card draft-feed dr-feed">
                  <h4 className="dr-card-title">{fr ? 'Derniers choix' : 'Latest picks'}</h4>
                  {recent.length === 0 && <div className="dr-team-empty">{t.noPicksYet}</div>}
                  {recent.map((p, i) => {
                    const m = members.find((x) => x.user_id === p.user_id);
                    return (
                      <div key={p.id} className={`dr-feed-item ${i === 0 ? 'latest' : ''}`}>
                        <span className="dr-feed-n">{p.pick_number != null ? `#${p.pick_number}` : (fr ? 'ÉQ' : 'TM')}</span>
                        <span className="dr-feed-player"><b>{p.player_name}</b> <small>{p.position} · {p.team}</small></span>
                        <span className="dr-feed-by">{m?.profiles?.username || '—'}</span>
                      </div>
                    );
                  })}
                </section>
            </div>

            <div className={`dr-layer dr-layer-teams ${sideTab === 'teams' ? 'on' : ''}`}>
              <section className="dr-card dr-team-picker">
                <h4 className="dr-card-title">{fr ? 'Équipes' : 'Teams'}</h4>
                <div className="dr-team-list">
                  {members.map((m) => {
                    const c = countPos(picksByUser[m.user_id] || []);
                    const n = (picksByUser[m.user_id] || []).length;
                    return (
                      <button
                        key={m.user_id}
                        className={`dr-team-btn ${m.user_id === lineupId ? 'active' : ''} ${m.draft_slot === currentSlot ? 'current' : ''} ${m.user_id === userId ? 'me' : ''}`}
                        onClick={() => setViewTeam(m.user_id)}
                      >
                        <span className="dr-team-slot">#{m.draft_slot}</span>
                        <span className="dr-team-name"><TeamAvatar team={teamOf[m.user_id]} name={m.profiles?.username} size={24} /> {m.profiles?.username}</span>
                        <span className="dr-team-pos">
                          {c.F}{hasSlots && slotLimit.F != null ? `/${slotLimit.F}` : ''}F · {c.D}{hasSlots && slotLimit.D != null ? `/${slotLimit.D}` : ''}D · {c.G}{hasSlots && slotLimit.G != null ? `/${slotLimit.G}` : ''}G
                        </span>
                        <span className="dr-team-count">{n}/{rosterSize}</span>
                      </button>
                    );
                  })}
                </div>
              </section>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

const TR = {
  en: {
    back: 'Back to lobby',
    loading: 'Loading draft…',
    loadingPlayers: 'Loading players…',
    round: 'Round',
    pick: 'Pick',
    onClock: 'On the clock',
    you: 'YOU',
    searchPlayer: 'Search player…',
    allPos: 'All positions',
    forwards: 'Forwards',
    defense: 'Defense',
    goalies: 'Goalies',
    allTeams: 'All teams',
    pts: 'pts',
    notYourTurn: 'Not your turn',
    noPlayers: 'No players match filters',
    showingFirst: 'Showing first',
    pickFeed: 'Pick feed',
    noPicksYet: 'No picks yet',
    draftComplete: 'Draft complete — pool is now active.',
    errRosters: 'Could not load NHL players',
    errPick: 'Pick failed',
    youAreSelecting: 'You are selecting…',
    isSelecting: 'is selecting…',
    confirmPick: 'Confirm Pick',
    changeHint: 'Click another player to change',
    cancelPool: 'Cancel Pool',
    confirmCancel: 'Cancel this pool? All entry fees will be refunded and the draft will be stopped.',
    errCancel: 'Could not cancel pool',
    hideInjured: 'Hide injured',
    showMyQueue: 'Show my queue',
    inYourQueue: 'In your queue',
    draftedBy: 'drafted by',
    autoPickFromList: 'Pick from my list:',
    autoPickHint: 'Stages your highest-ranked available queued player. You still confirm.',
    playoffOnlyNote: 'Showing only players from teams in the playoffs.',
    noPlayoffData: 'Playoff team data unavailable — showing all teams.',
  },
  fr: {
    back: 'Retour au lobby',
    loading: 'Chargement du repêchage…',
    loadingPlayers: 'Chargement des joueurs…',
    round: 'Ronde',
    pick: 'Choix',
    onClock: 'Au tour de',
    you: 'VOUS',
    searchPlayer: 'Chercher un joueur…',
    allPos: 'Toutes positions',
    forwards: 'Attaquants',
    defense: 'Défense',
    goalies: 'Gardiens',
    allTeams: 'Toutes équipes',
    pts: 'pts',
    notYourTurn: "Pas votre tour",
    noPlayers: 'Aucun joueur ne correspond',
    showingFirst: 'Affichage des premiers',
    pickFeed: 'Fil des choix',
    noPicksYet: 'Aucun choix encore',
    draftComplete: 'Repêchage terminé — le pool est actif.',
    errRosters: 'Impossible de charger les joueurs NHL',
    errPick: 'Choix refusé',
    youAreSelecting: 'Vous sélectionnez…',
    isSelecting: 'sélectionne…',
    confirmPick: 'Confirmer',
    changeHint: 'Cliquez un autre joueur pour changer',
    cancelPool: 'Annuler le pool',
    confirmCancel: "Annuler ce pool? Tous les frais seront remboursés et le repêchage sera arrêté.",
    errCancel: "Impossible d'annuler le pool",
    hideInjured: 'Masquer les blessés',
    showMyQueue: 'Ma liste',
    inYourQueue: 'Dans votre liste',
    draftedBy: 'choisi par',
    autoPickFromList: 'Choisir depuis ma liste :',
    autoPickHint: 'Stage votre meilleur joueur disponible. Vous devez encore confirmer.',
    playoffOnlyNote: 'Affichage limité aux joueurs des équipes en séries.',
    noPlayoffData: 'Données de séries indisponibles — affichage de toutes les équipes.',
  },
};
